/**
 * View builders — plain DOM, no framework. Each returns an element tree and
 * wires callbacks the controller supplies. Keeping these pure-ish makes the
 * controller (main.ts) a small state machine.
 */
import type { Board, Clue, SessionStats, TagInfo, TagSkill } from '../engine/types';
import { Grade } from '../engine/types';
import { modernValue } from '../engine/value';
import type { CategoryInfo, DeckInfo } from '../data/repo';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

/** A clue card showing the question (answer hidden). Caller wires onclick. */
function questionCard(clue: Clue, showValue: boolean): HTMLDivElement {
  const card = el('div', 'clue-card');
  card.append(el('div', 'clue-cat', clue.c));
  if (showValue && clue.v > 0) {
    card.append(el('div', 'clue-value', `$${clue.v}${clue.dd ? ' · Daily Double' : ''}`));
  }
  const body = el('div', 'clue-body');
  const text = el('div', 'clue-text');
  text.dataset.fit = 'fill';
  text.textContent = clue.q;
  body.append(text);
  card.append(body);
  card.append(el('div', 'reveal-hint', 'Tap to reveal answer'));
  return card;
}

/** A clue card with the question on top and the revealed response below. */
function revealCard(clue: Clue): HTMLDivElement {
  const card = el('div', 'clue-card revealed');
  card.style.cursor = 'default';
  card.append(el('div', 'clue-cat', clue.c));

  const clueRegion = el('div', 'clue-region');
  const text = el('div', 'clue-text');
  text.dataset.fit = 'fill';
  text.textContent = clue.q;
  clueRegion.append(text);

  const answerRegion = el('div', 'answer-region');
  answerRegion.append(el('span', 'lead', 'Correct response'));
  const box = el('div', 'answer-box');
  const ans = el('div', 'answer');
  ans.dataset.fit = 'fill';
  ans.textContent = clue.a;
  box.append(ans);
  answerRegion.append(box);

  card.append(clueRegion, answerRegion);
  return card;
}

export function loadingView(): { root: HTMLElement; set: (pct: number, phase: string) => void } {
  const root = el('div', 'screen center');
  root.append(el('div', 'title', 'JEOPARDY'));
  const loader = el('div', 'loader');
  const phase = el('div', 'phase', 'Starting up…');
  const bar = el('div', 'bar');
  const fill = el('i');
  bar.append(fill);
  loader.append(bar, phase);
  root.append(loader);
  return {
    root,
    set: (pct, p) => {
      fill.style.width = `${Math.round(pct * 100)}%`;
      phase.textContent = p;
    },
  };
}

function skillColor(skill: TagSkill | undefined): string {
  if (!skill || skill.attempts === 0) return '#555';
  // red (weak) -> gold -> green (strong)
  const r = skill.rating;
  if (r < 0.4) return '#b3372e';
  if (r < 0.65) return '#d29d4d';
  return '#1b7a36';
}

export function homeView(opts: {
  clueCount: number;
  lifetime: { presented: number; known: number; shaky: number; missed: number };
  onAdaptive: () => void;
  onBoard: () => void;
  onCategory: () => void;
  onTopic: () => void;
  onFocus: () => void;
  onReset: () => void;
}): HTMLElement {
  const root = el('div', 'screen home');

  const head = el('div', 'home-head');
  head.append(el('h1', 'title', 'JEOPARDY'));
  head.append(el('div', 'sub', `Champion Trainer · ${opts.clueCount.toLocaleString()} clues`));
  root.append(head);

  const lt = opts.lifetime;
  if (lt.presented > 0) {
    const acc = Math.round((100 * (lt.known + 0.5 * lt.shaky)) / lt.presented);
    const line = el('div', 'statline');
    line.innerHTML = `<span><b>${lt.presented.toLocaleString()}</b> seen</span><span><b>${acc}%</b> accuracy</span><span><b>${lt.missed.toLocaleString()}</b> missed</span>`;
    root.append(line);
  }

  const menu = el('div', 'home-menu');
  const mk = (cls: string, title: string, sub: string, onClick: () => void) => {
    const b = el('button', `big-btn ${cls}`);
    b.append(Object.assign(el('span'), { textContent: title }), el('small', undefined, sub));
    b.onclick = onClick;
    return b;
  };
  menu.append(
    mk('', '▶ Adaptive Practice', 'A weak-area category, then its 5 clues', opts.onAdaptive),
    mk('focus-btn', '◎ Focus Areas', 'Art, classic movies, music, spirits, presidents…', opts.onFocus),
    mk('board-btn', '▦ Play a Board', 'A real 6-category board, scored in dollars', opts.onBoard),
    mk('topic-btn', '◉ Drill a Topic', 'One of 43 subjects — see your weak spots', opts.onTopic),
    mk('cat-btn', '⌖ Exact Category', 'One original show category, e.g. BEFORE & AFTER', opts.onCategory),
  );
  root.append(menu);

  const reset = el('button', 'reset-link', 'Reset progress');
  reset.onclick = opts.onReset;
  root.append(reset);

  return root;
}

/** Browse/select one of the 43 topic tags (with weakness pips). */
export function topicPickerView(opts: {
  tags: TagInfo[];
  skills: Map<number, TagSkill>;
  onPick: (tagId: number) => void;
  onBack: () => void;
}): HTMLElement {
  const root = el('div', 'screen');
  const top = el('div', 'play-top');
  const back = el('button', 'quit', '‹ Back');
  back.onclick = opts.onBack;
  top.append(back, el('span', undefined, 'Drill a topic'));
  root.append(top);

  const list = el('div', 'tag-list');
  for (const t of opts.tags) {
    const row = el('button', 'tag-row');
    const pip = el('span', 'skillpip');
    pip.style.background = skillColor(opts.skills.get(t.id));
    row.append(pip, el('span', undefined, t.name), el('span', 'count', t.count.toLocaleString()));
    row.onclick = () => opts.onPick(t.id);
    list.append(row);
  }
  root.append(list);
  return root;
}

export interface PlayCallbacks {
  onReveal: () => void;
  onGrade: (g: Grade) => void;
  onQuit: () => void;
}

export function playView(
  clue: Clue,
  modeLabel: string,
  stats: SessionStats,
  cb: PlayCallbacks,
): HTMLElement {
  const root = el('div', 'screen play');

  const top = el('div', 'play-top');
  const quit = el('button', 'quit', '✕ End');
  quit.onclick = cb.onQuit;
  const score = el('span', undefined, `${stats.presented} done · ${stats.known}✓ ${stats.missed}✗`);
  const chip = el('span', 'modechip', modeLabel);
  top.append(quit, score, chip);
  root.append(top);

  const card = questionCard(clue, true);
  card.onclick = cb.onReveal;
  root.append(card);

  return root;
}

export function revealView(
  clue: Clue,
  modeLabel: string,
  stats: SessionStats,
  cb: PlayCallbacks,
): HTMLElement {
  const root = el('div', 'screen play');

  const top = el('div', 'play-top');
  const quit = el('button', 'quit', '✕ End');
  quit.onclick = cb.onQuit;
  const score = el('span', undefined, `${stats.presented} done · ${stats.known}✓ ${stats.missed}✗`);
  const chip = el('span', 'modechip', modeLabel);
  top.append(quit, score, chip);
  root.append(top);

  root.append(revealCard(clue));

  const grades = el('div', 'grade-row');
  const known = el('button', 'g-known');
  known.append(Object.assign(el('span'), { textContent: 'Knew it' }), el('small', undefined, 'cold'));
  known.onclick = () => cb.onGrade(Grade.Known);
  const shaky = el('button', 'g-shaky');
  shaky.append(Object.assign(el('span'), { textContent: 'Got it' }), el('small', undefined, 'but shaky'));
  shaky.onclick = () => cb.onGrade(Grade.Shaky);
  const missed = el('button', 'g-missed');
  missed.append(Object.assign(el('span'), { textContent: 'Missed' }), el('small', undefined, 'it'));
  missed.onclick = () => cb.onGrade(Grade.Missed);
  grades.append(known, shaky, missed);
  root.append(grades);

  return root;
}

export function summaryView(opts: {
  stats: SessionStats;
  weak: { name: string; rating: number }[];
  onHome: () => void;
  onAgain: () => void;
}): HTMLElement {
  const root = el('div', 'screen center summary');
  root.append(el('h2', 'title', 'Session Complete'));

  const s = opts.stats;
  const tallies = el('div', 'tallies');
  const mk = (cls: string, n: number, label: string) => {
    const t = el('div', `tally ${cls}`);
    t.append(el('div', 'n', String(n)), el('div', undefined, label));
    return t;
  };
  tallies.append(mk('known', s.known, 'Knew it'), mk('', s.shaky, 'Shaky'), mk('missed', s.missed, 'Missed'));
  root.append(tallies);

  if (s.presented > 0) {
    const acc = Math.round((100 * (s.known + 0.5 * s.shaky)) / s.presented);
    root.append(el('div', undefined, `${s.presented} clues · ${acc}% accuracy`));
  }

  if (opts.weak.length > 0) {
    const box = el('div', 'weakbox');
    box.append(el('h3', undefined, 'Focus areas'));
    for (const w of opts.weak) {
      const row = el('div', 'w');
      const bar = el('div', 'wbar');
      const fill = el('i');
      fill.style.width = `${Math.round((1 - w.rating) * 100)}%`;
      bar.append(fill);
      row.append(el('span', undefined, w.name), bar);
      box.append(row);
    }
    root.append(box);
  }

  const again = el('button', 'big-btn');
  again.textContent = '▶ Keep practicing';
  again.onclick = opts.onAgain;
  const home = el('button', 'tag-row');
  home.style.justifyContent = 'center';
  home.textContent = 'Back to menu';
  home.onclick = opts.onHome;
  root.append(again, home);

  return root;
}

/** Curated focus decks for common weak areas. */
export function focusPickerView(opts: {
  decks: DeckInfo[];
  onPick: (deck: DeckInfo) => void;
  onBack: () => void;
}): HTMLElement {
  const root = el('div', 'screen');
  const top = el('div', 'play-top');
  const back = el('button', 'quit', '‹ Back');
  back.onclick = opts.onBack;
  top.append(back, el('span', undefined, 'Focus areas'));
  root.append(top);

  const list = el('div', 'tag-list');
  for (const d of opts.decks) {
    const row = el('button', 'deck-row');
    const txt = el('div', 'deck-txt');
    txt.append(el('div', 'deck-name', d.name), el('div', 'deck-blurb', d.blurb));
    row.append(txt, el('span', 'count', `${d.count.toLocaleString()}`));
    row.onclick = () => opts.onPick(d);
    list.append(row);
  }
  root.append(list);
  return root;
}

// ---- adaptive: category intro ----

export function categoryIntroView(opts: {
  name: string;
  stats: SessionStats;
  onStart: () => void;
  onQuit: () => void;
}): HTMLElement {
  const root = el('div', 'screen play');

  const top = el('div', 'play-top');
  const quit = el('button', 'quit', '✕ End');
  quit.onclick = opts.onQuit;
  const score = el('span', undefined, `${opts.stats.presented} done · ${opts.stats.known}✓ ${opts.stats.missed}✗`);
  top.append(quit, score, el('span', 'modechip', 'Adaptive'));
  root.append(top);

  const card = el('div', 'clue-card intro-card');
  card.append(el('div', 'intro-label', 'Your category'));
  const body = el('div', 'clue-body');
  const name = el('div', 'intro-name');
  name.dataset.fit = 'fill';
  name.textContent = opts.name;
  body.append(name);
  card.append(body);
  card.append(el('div', 'reveal-hint', '5 clues · tap to begin'));
  card.onclick = opts.onStart;
  root.append(card);

  return root;
}

// ---- drill-by-category picker ----

export function categoryPickerView(opts: {
  initial: CategoryInfo[];
  onSearch: (q: string) => CategoryInfo[];
  onPick: (title: string) => void;
  onBack: () => void;
}): HTMLElement {
  const root = el('div', 'screen');
  const top = el('div', 'play-top');
  const back = el('button', 'quit', '‹ Back');
  back.onclick = opts.onBack;
  top.append(back, el('span', undefined, 'Pick an exact category'));
  root.append(top);

  const search = document.createElement('input');
  search.className = 'cat-search';
  search.placeholder = 'Search 48,000+ categories…';
  search.autocapitalize = 'characters';
  root.append(search);

  const list = el('div', 'tag-list');
  const render = (cats: CategoryInfo[]) => {
    list.replaceChildren();
    if (cats.length === 0) {
      list.append(el('div', 'empty', 'No categories with ≥5 clues match.'));
      return;
    }
    for (const c of cats) {
      const row = el('button', 'tag-row');
      row.append(el('span', undefined, c.title), el('span', 'count', `${c.count}`));
      row.onclick = () => opts.onPick(c.title);
      list.append(row);
    }
  };
  render(opts.initial);

  let timer: ReturnType<typeof setTimeout> | undefined;
  search.oninput = () => {
    clearTimeout(timer);
    timer = setTimeout(() => render(opts.onSearch(search.value)), 150);
  };
  root.append(list);
  return root;
}

// ---- authentic board mode ----

export interface BoardState {
  used: Set<number>;
  score: number;
}

export function boardView(
  board: Board,
  state: BoardState,
  cb: { onCell: (clue: Clue) => void; onQuit: () => void },
): HTMLElement {
  const root = el('div', 'screen board-screen');

  const top = el('div', 'play-top');
  const quit = el('button', 'quit', '✕ End');
  quit.onclick = cb.onQuit;
  const score = el('span', 'board-score', `$${state.score.toLocaleString()}`);
  const meta = el('span', 'modechip', `${board.round === 'J' ? 'Jeopardy' : 'Double'} · ${board.date}`);
  top.append(quit, score, meta);
  root.append(top);

  const grid = el('div', 'board-grid');
  // Header row — each category name auto-sized to fill its cell.
  for (const c of board.categories) {
    const head = el('div', 'board-cat');
    const span = el('span');
    span.dataset.fit = 'fill';
    span.textContent = c.name;
    head.append(span);
    grid.append(head);
  }
  // 5 value rows.
  for (let row = 0; row < 5; row++) {
    for (const c of board.categories) {
      const clue = c.clues[row];
      if (!clue) {
        grid.append(el('div', 'board-cell empty'));
        continue;
      }
      const cell = el('div', 'board-cell');
      if (state.used.has(clue.i)) {
        cell.classList.add('done');
      } else {
        const span = el('span');
        span.dataset.fit = 'val';
        span.textContent = `$${modernValue(clue)}`;
        cell.append(span);
        cell.onclick = () => cb.onCell(clue);
      }
      grid.append(cell);
    }
  }
  root.append(grid);
  root.append(el('div', 'board-hint', 'Tap a dollar value to play that clue'));
  return root;
}

export function boardClueView(
  clue: Clue,
  value: number,
  score: number,
  cb: { onReveal: () => void; onQuit: () => void },
): HTMLElement {
  const root = el('div', 'screen play');
  const top = el('div', 'play-top');
  const quit = el('button', 'quit', '‹ Board');
  quit.onclick = cb.onQuit;
  top.append(quit, el('span', 'board-score', `$${score.toLocaleString()}`), el('span', 'modechip', `$${value}`));
  root.append(top);

  const card = questionCard(clue, false);
  card.onclick = cb.onReveal;
  root.append(card);
  return root;
}

export function boardRevealView(
  clue: Clue,
  value: number,
  score: number,
  cb: { onGrade: (g: Grade, correct: boolean) => void; onQuit: () => void },
): HTMLElement {
  const root = el('div', 'screen play');
  const top = el('div', 'play-top');
  const quit = el('button', 'quit', '‹ Board');
  quit.onclick = cb.onQuit;
  top.append(quit, el('span', 'board-score', `$${score.toLocaleString()}`), el('span', 'modechip', `$${value}`));
  root.append(top);

  root.append(revealCard(clue));

  const grades = el('div', 'grade-row');
  const known = el('button', 'g-known');
  known.append(Object.assign(el('span'), { textContent: `+$${value}` }), el('small', undefined, 'knew it'));
  known.onclick = () => cb.onGrade(Grade.Known, true);
  const shaky = el('button', 'g-shaky');
  shaky.append(Object.assign(el('span'), { textContent: `+$${value}` }), el('small', undefined, 'got it, shaky'));
  shaky.onclick = () => cb.onGrade(Grade.Shaky, true);
  const missed = el('button', 'g-missed');
  missed.append(Object.assign(el('span'), { textContent: `−$${value}` }), el('small', undefined, 'missed it'));
  missed.onclick = () => cb.onGrade(Grade.Missed, false);
  grades.append(known, shaky, missed);
  root.append(grades);
  return root;
}

export function boardSummaryView(opts: {
  score: number;
  maxScore: number;
  date: string;
  correct: number;
  total: number;
  onAnother: () => void;
  onHome: () => void;
}): HTMLElement {
  const root = el('div', 'screen center summary');
  root.append(el('h2', 'title', 'Board Complete'));
  const sc = el('div', 'board-final');
  sc.textContent = `$${opts.score.toLocaleString()}`;
  if (opts.score < 0) sc.classList.add('neg');
  root.append(sc);
  root.append(
    el('div', undefined, `${opts.correct}/${opts.total} correct · max possible $${opts.maxScore.toLocaleString()}`),
  );
  root.append(el('div', 'sub', `Aired ${opts.date}`));

  const again = el('button', 'big-btn');
  again.textContent = '▦ Another board';
  again.onclick = opts.onAnother;
  const home = el('button', 'tag-row');
  home.style.justifyContent = 'center';
  home.textContent = 'Back to menu';
  home.onclick = opts.onHome;
  root.append(again, home);
  return root;
}
